import { FunctionComponent, useState } from "react";
import { 
    Button, 
    Menu, 
    MenuItem, 
    Typography, 
    Badge,
    Box
} from "@mui/material";
import CheckIcon from '@mui/icons-material/Check';
import styles from "./TicketFilterDropdown.module.css";

interface FilterOption {
    id: string;
    label: string;
}

interface TicketFilterDropdownProps {
    label: string;
    options: FilterOption[];
    selectedValue: string | string[] | null;
    onSelect: (id: string) => void;
    onClear: () => void;
    icon?: React.ReactNode;
    styleValues: {
        Height: string;
    }
}

const TicketFilterDropdown: FunctionComponent<TicketFilterDropdownProps> = ({
    label,
    options,
    selectedValue,
    onSelect,
    onClear,
    icon,
    styleValues
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleOptionSelect = (id: string) => {
        onSelect(id);
    };

    const isSelected = (id: string) => {
        if (Array.isArray(selectedValue)) {
            return selectedValue.includes(id);
        }
        return selectedValue === id;
    };

    const selectedCount = Array.isArray(selectedValue) 
        ? selectedValue.length 
        : selectedValue ? 1 : 0;

    return (
        <div>
            <Button
                variant="outlined"
                onClick={handleClick}
                className={`${styles.filterButton} ${selectedCount > 0 ? styles.activeButton : ""}`}
                startIcon={icon}
                disableRipple
            >
                <span className={styles.buttonLabel}>{label}</span>
                {selectedCount > 0 && (
                    <span className={styles.badge}>{selectedCount}</span>
                )}
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    className: styles.menuPaper
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <div className={styles.menuHeader}>
                    <Typography className={styles.menuTitle}>{label}</Typography>
                </div>
                <div 
                    className={styles.optionsList}
                    style={{
                        height: styleValues.Height
                    }}
                >
                    {options.map((option) => (
                        <MenuItem 
                            key={option.id} 
                            onClick={() => handleOptionSelect(option.id)}
                            className={`${styles.menuItem} ${isSelected(option.id) ? styles.selectedItem : ""}`}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: '10px' }}>
                                <CheckIcon className={`${styles.checkIcon} ${isSelected(option.id) ? styles.show : ""}`} />
                                <Typography className={styles.optionLabel}>{option.label}</Typography>
                            </Box>
                        </MenuItem>
                    ))}
                </div>
                <MenuItem onClick={() => { onClear(); handleClose(); }} className={styles.clearItem}>
                    <Typography className={styles.clearText}>Clear {label.toLowerCase()}</Typography>
                </MenuItem>
            </Menu>
        </div>
    );
};

export default TicketFilterDropdown;
